/**
 * Money (doctrine 3): stored and passed as integer CENTS everywhere. Two renderers:
 *   money()      — screen/invoice/API text: "$62.50"
 *   moneyWords() — the VOICE channel: "sixty-two dollars and fifty cents"
 *
 * moneyWords exists because of a real production failure: a live caller heard a digit
 * string read back and said "I couldn't understand the numbers you just said." The
 * module's prompt now hard-codes "say money amounts in words"; this makes it mechanical
 * rather than a prompt instruction the model can drift away from.
 *
 * Related production incident (v1.3 §4.5): a BSS adapter passed cents through unconverted
 * and $35.33 was spoken as "three thousand five hundred thirty-three dollars." The guard
 * against that is the branded Cents type plus adapter unit-contract tests — a speech-layer
 * check cannot catch it, because the wrong number WAS the tool output.
 */

declare const centsBrand: unique symbol;
/** Integer cents. Construct via cents() so a bare number can't drift in as dollars. */
export type Cents = number & { readonly [centsBrand]: true };

export function cents(n: number): Cents {
  if (!Number.isInteger(n)) throw new Error(`Cents must be an integer, got ${n}`);
  return n as Cents;
}

/** Only for adapter boundaries that genuinely receive dollars (rare). */
export function centsFromDollars(dollars: number): Cents {
  return cents(Math.round(dollars * 100));
}

export function money(c: Cents | number): string {
  const v = Number(c);
  const neg = v < 0;
  const abs = Math.abs(v);
  const whole = Math.floor(abs / 100);
  const frac = String(abs % 100).padStart(2, '0');
  const grouped = whole.toLocaleString('en-US');
  return `${neg ? '-' : ''}$${grouped}.${frac}`;
}

const ONES = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine',
  'ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen',
  'eighteen', 'nineteen'];
const TENS = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];

function under1000(n: number): string {
  const parts: string[] = [];
  const h = Math.floor(n / 100);
  const rest = n % 100;
  if (h) parts.push(`${ONES[h]} hundred`);
  if (rest < 20) {
    if (rest || !h) parts.push(ONES[rest] as string);
  } else {
    const t = Math.floor(rest / 10);
    const o = rest % 10;
    parts.push(o ? `${TENS[t]}-${ONES[o]}` : (TENS[t] as string));
  }
  return parts.join(' ');
}

export function numberToWords(n: number): string {
  if (n === 0) return 'zero';
  if (n < 0) return `negative ${numberToWords(-n)}`;
  const scales: Array<[number, string]> = [
    [1_000_000_000, 'billion'],
    [1_000_000, 'million'],
    [1_000, 'thousand'],
  ];
  const parts: string[] = [];
  let rest = n;
  for (const [value, name] of scales) {
    if (rest >= value) {
      parts.push(`${numberToWords(Math.floor(rest / value))} ${name}`);
      rest %= value;
    }
  }
  if (rest > 0) parts.push(under1000(rest));
  return parts.join(' ');
}

/** Voice channel rendering. money() is for eyes; this is for ears. */
export function moneyWords(c: Cents | number): string {
  const v = Number(c);
  const neg = v < 0;
  const abs = Math.abs(v);
  const dollars = Math.floor(abs / 100);
  const rem = abs % 100;
  const d = `${numberToWords(dollars)} dollar${dollars === 1 ? '' : 's'}`;
  const body = rem === 0 ? d : `${d} and ${numberToWords(rem)} cent${rem === 1 ? '' : 's'}`;
  return neg ? `negative ${body}` : body;
}
